import { useContext } from 'react';

import { useArgoContext } from '@amzn/awsscm-argo-core';

import * as log from 'loglevel';

import { useMetricsProfiler } from './boost/useMetrics';
import { AppContext, AppType } from './store/appContext';

const logger = log;

// There is a bug inside Boost App Context with metrics publisher, setting default context until its integrated
const DefaultMetricsContext = {
    initialMetricsPublisher: {
        newChildActionPublisherForMethod: (p0: string) => ({
            publishCounterMonitor: () => { },
            publishString: () => { },
        }),
    },
    user: {
        username: '',
    },
    logger,
};

const useBoostMetricsContext = () => {
    const metricsProfiler = useMetricsProfiler();

    if (!metricsProfiler?.counterMetrics) {
        // appContext.appType === "BOOST" && console.error("Boost Metrics context not set")
        return DefaultMetricsContext;
    }

    const networkErrorsPublisher = (count: number) =>
        metricsProfiler?.counterMetrics({
            method: 'networkErrors',
            metricName: 'networkErrors',
            metricValue: count,
            timeStamp: new Date(),
        });
    const graphQLErrorsPublisher = (count: number) =>
        metricsProfiler?.counterMetrics({
            method: 'graphQLErrors',
            metricName: 'graphQLErrors',
            metricValue: count,
            timeStamp: new Date(),
        });

    const latencyPublisher = (count: number) =>
        metricsProfiler?.counterMetrics({
            method: 'latency',
            metricName: 'latency',
            metricValue: count,
            timeStamp: new Date(),
        });

    return {
        networkErrorsPublisher,
        graphQLErrorsPublisher,
        latencyPublisher,
        initialMetricsPublisher: {
            newChildActionPublisherForMethod: (method: string) => ({
                publishCounterMonitor: (metricName: string, metricValue: number, timeStamp?: Date) =>
                    metricsProfiler?.counterMetrics({ method, metricName, metricValue, timeStamp: new Date() }),
                publishString: (metricName: string, metricValue: number) =>
                    metricsProfiler?.counterMetrics({ method, metricName, metricValue }),
                publishTimerMonitor: (metricName: string, metricValue: number) =>
                    metricsProfiler?.timerMetrics({ method, metricName, metricValue }),
            }),
        },
        user: {
            username: '',
        },
        logger,
    };
};

export const useArgoMetrics = () => {
    const context = useArgoContext();
    const newChildActionPublisherForMethod = context.initialMetricsPublisher.newChildActionPublisherForMethod;
    const initialMetricsPublisher = context.initialMetricsPublisher;
    const networkErrorsPublisher = context.initialMetricsPublisher.newChildActionPublisherForMethod('networkErrors');
    const graphQLErrorsPublisher = context.initialMetricsPublisher.newChildActionPublisherForMethod('graphQLErrors');

    const latencyPublisher = context.initialMetricsPublisher.newChildActionPublisherForMethod('latency');
    return {
        initialMetricsPublisher,
        networkErrorsPublisher,
        graphQLErrorsPublisher,
        latencyPublisher,
        newChildActionPublisherForMethod,
    };
};

export function useMetrics() {
    const appContext = useContext(AppContext);
    const contextSelector = appContext.appType === AppType.ARGO ? useArgoContext : useBoostMetricsContext;
    const context = contextSelector();

    const newChildActionPublisherForMethod = context?.initialMetricsPublisher?.newChildActionPublisherForMethod;
    const initialMetricsPublisher = context?.initialMetricsPublisher;
    const networkErrorsPublisher = context?.initialMetricsPublisher?.newChildActionPublisherForMethod('networkErrors');
    const graphQLErrorsPublisher = context?.initialMetricsPublisher?.newChildActionPublisherForMethod('graphQLErrors');

    const latencyPublisher = context?.initialMetricsPublisher?.newChildActionPublisherForMethod('latency');
    return {
        initialMetricsPublisher,
        networkErrorsPublisher,
        graphQLErrorsPublisher,
        latencyPublisher,
        newChildActionPublisherForMethod,
    };
}

function useGetUserInformation() {
    const appContext = useContext(AppContext);
    const user = appContext?.context.user?.username || '';

    // user signOut is only supported for Argo
    const signOut = appContext?.context.user?.signOut;
    return {
        user,
        signOut,
    };
}

function useLogger() {
    const appContext = useContext(AppContext);
    const contextSelector = appContext.appType === AppType.ARGO ? useArgoContext : useBoostMetricsContext;
    return contextSelector().logger;
}

export { useArgoContext, logger, useLogger, useGetUserInformation, useBoostMetricsContext };
